import { ComponentProps, PartData, RichText } from '@enonic/react-components';
import { BackLink } from '/lib/components/backLink/backLink';
import './subpage.css';
import '/site/common.css';
import {
	ImageWithTextBox,
	JobListing,
	RichTextType,
} from '/react4xp/components/parts/frontpage/Frontpage';
import { Alert, Heading, Paragraph } from '@digdir/designsystemet-react';
import { toArray } from '/react4xp/utils/arrayUtils';
import { ImageTextBox } from '/lib/components/imageTextBox/imageTextBox';
import { QuoteBox, QuoteBoxType } from '/lib/components/quoteBox/quoteBox';
import { JobAd } from '/lib/components/jobAd/jobAd';
import {
	LinkButtonContent,
	renderLinkButtonWrapper,
} from '/lib/components/renderLinkButtonWrapper/renderLinkButtonWrapper';

interface SubPageData {
	title: string;
	ingress: string;
	topImage: Image;
	parentLink: string;
	elements: SubpageElement[];
}

interface Image {
	altText: string;
	caption: string;
	url: string;
}

interface RichTextWrapperType {
	_selected: 'paragraph';
	text: RichTextType;
}

export interface TextboxWrapperType {
	_selected: 'textboxWrapper';
	texts: RichTextType[];
}

interface QuoteBoxWrapperType {
	_selected: 'quoteBox';
	quoteBox: QuoteBoxType;
}

interface JobAdsWrapperType {
	_selected: 'vacancies';
	vacancies: { jobHeading: string; zeroJobs: string };
	jobAds: JobListing[];
}

interface LinkButtonWrapperType {
	linkButtonWrapper: { headingTextInBox: string; linkButtons: any[] };
	_selected: 'linkButtonWrapper';
	linkButtons: LinkButtonContent[];
}

interface VideoType {
	vimeoVideo: any;
	youtubeVideo: any;
	_selected: 'vimeoVideo' | 'youtubeVideo';
}

interface VideoDataType {
	videoTitle: string;
	description: string;
	videoType: VideoType;
	videokode: string;
	width: number;
	height: number;
}

interface VideoWrapperType {
	_selected: 'video';
	video: VideoDataType;
}

type SubpageElement =
	| RichTextWrapperType
	| TextboxWrapperType
	| ImageWithTextBox
	| QuoteBoxWrapperType
	| JobAdsWrapperType
	| LinkButtonWrapperType
	| VideoWrapperType;

export const Subpage = (props: ComponentProps<PartData>) => {
	const { title, ingress, topImage, parentLink, elements } =
		props.data as any as SubPageData;

	return (
		<div className="subpage">
			<div className="page-inner">
				<BackLink parentLink={parentLink} />
			</div>
			<main id="main-content">
				<section className="subpage-top">
					<div className="page-inner">
						<div>
							<h1>{title}</h1>
							<p className="ds-paragraph">{ingress}</p>
						</div>
						<img src={topImage.url} alt={topImage.altText} />
					</div>
				</section>
				<div className="subpage-content">
					<div className="page-inner">
						{elements.map((element, index) => {
							switch (element._selected) {
								case 'paragraph':
									return (
										<RichText
											data={element.text}
											meta={props.meta}
											common={props.common}
											component={props.component}
										/>
									);
								case 'textboxWrapper':
									return (
										<section
											key={index}
											className="textbox-wrapper"
										>
											{toArray(element.texts).map(
												(text, tbIndex) => {
													return (
														<Paragraph
															key={tbIndex}
														>
															<RichText
																data={text}
																meta={
																	props.meta
																}
																common={
																	props.common
																}
																component={
																	props.component
																}
															/>
														</Paragraph>
													);
												},
											)}
										</section>
									);
								case 'imageWithTextBox':
									return (
										<section
											key={index}
											className="image-with-textbox"
										>
											<ImageTextBox props={element} />
										</section>
									);
								case 'quoteBox':
									return (
										<section key={index}>
											<QuoteBox props={element} />
										</section>
									);
								case 'vacancies':
									return (
										<section key={index}>
											<Heading level={2}>
												{element.vacancies.jobHeading}
											</Heading>
											<div className="job-ad-wrapper">
												{!element.jobAds ? (
													<Alert data-color="warning">
														Vi kan ikke vise
														stillingsannonser nå.
														Prøv igjen senere.
													</Alert>
												) : element.jobAds.length >
												  0 ? (
													<div className="jobs">
														{element.jobAds.map(
															(job) => (
																<JobAd
																	key={job.id}
																	{...job}
																/>
															),
														)}
													</div>
												) : (
													<Paragraph>
														{
															element.vacancies
																.zeroJobs
														}
													</Paragraph>
												)}
											</div>
										</section>
									);
								case 'linkButtonWrapper':
									return (
										<div
											id={`${element._selected}-${index}`}
											key={index}
											className="link-button-wrapper"
										>
											<Heading level={2}>
												{
													element.linkButtonWrapper
														.headingTextInBox
												}
											</Heading>{' '}
											<div className="card-container">
												{renderLinkButtonWrapper(
													element.linkButtons,
												)}
											</div>
										</div>
									);
								case 'video': {
									let videoUrl = '';
									const videoTitle = element.video.videoTitle;
									if (
										element.video.videoType._selected ===
										'vimeoVideo'
									) {
										videoUrl = `https://player.vimeo.com/video/${element.video.videokode}`;
									} else if (
										element.video.videoType._selected ===
										'youtubeVideo'
									) {
										// Parameteret rel=0 betyr at det vises tiles med videoer fra samme kanal etter avspilling
										videoUrl = `https://www.youtube-nocookie.com/embed/${element.video.videokode}?rel=0`;
									}
									const width: number = element.video.width;
									const height: number = element.video.height;
									const paddingBottom =
										(height / width) * 100;
									return (
										<div
											id={`${element._selected}-${index}`}
											key={index}
											className="video-div"
											style={{
												paddingBottom: `${paddingBottom}%`,
											}}
										>
											<iframe
												src={videoUrl}
												allowFullScreen
												title={videoTitle}
											></iframe>
										</div>
									);
								}
								default:
									return '';
							}
						})}
					</div>
				</div>
			</main>
		</div>
	);
};
